import React, { useCallback, useEffect, useState } from "react";
import { CurateResource, CurateResolveDto } from "../../../Types/Curate/CurateResource";
import { fetchCurateResourceBytes, resolveCurateResource } from "../../../API/Curate/Curate";
import DocumentViewer from "../DocumentViewer/DocumentViewer";

const withPdfPage = (url: string, page?: number): string => {
  if (!page || page < 1) {
    return url;
  }

  const hash = url.indexOf("#");
  const base = hash >= 0 ? url.slice(0, hash) : url;
  return `${base}#page=${page}`;
};

type ResourceViewerProps = {
  resource: CurateResource | null;
  clientId: string;
  onClose: () => void;
};

const ResourceViewer = ({ resource, clientId, onClose }: ResourceViewerProps) => {
  const [resolved, setResolved] = useState<CurateResolveDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const providerId = resource?.providerId || "mesma-url-bank";
  const resourceId = resource?.id;

  useEffect(() => {
    let cancelled = false;
    setResolved(null);
    setError(null);

    if (!resource || !clientId) {
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const result = await resolveCurateResource(
          providerId,
          resource.id,
          clientId,
          resource.page
        );
        if (cancelled) {
          return;
        }

        if (result.disposition === "external" && result.url) {
          window.open(withPdfPage(result.url, resource.page), "_blank", "noopener,noreferrer");
          onClose();
          return;
        }

        setResolved(result);
      } catch {
        if (!cancelled) {
          setError("Unable to open this resource.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resource, clientId, onClose, providerId]);

  const fetchBytes = useCallback(() => {
    if (!resourceId || !clientId) {
      return Promise.reject(new Error("Missing resource"));
    }
    return fetchCurateResourceBytes(providerId, resourceId, clientId);
  }, [clientId, providerId, resourceId]);

  if (!resource) {
    return null;
  }

  const fileName = resolved?.title || resource.Title || "Resource";
  const extension = resolved?.extension || resource.extension;

  return (
    <DocumentViewer
      fileName={fileName}
      extension={extension}
      url={resolved?.url}
      fetchBytes={fetchBytes}
      onClose={onClose}
      isVisible
      loading={loading}
      error={error}
    />
  );
};

export default ResourceViewer;
